import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdVisibility } from "react-icons/md";
import { toast } from "react-toastify";
import axios from "axios";
import {
  IconButton,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  Select,
  MenuItem,
  Pagination,
  Stack,
} from "@mui/material";
import { MdModeEdit, MdDelete } from "react-icons/md";
import SearchIcon from "@mui/icons-material/Search";

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        "https://ecom-api.applligentdemo.com/category/getCategory",
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      const data = response.data;

      if (Array.isArray(data.categories)) {
        const formatted = data.categories.map((cat) => ({
          ...cat,
          status: cat.status ? "Active" : "Inactive", // ✅ convert boolean to string
        }));
        setCategories(formatted);
      } else {
        console.error("Expected 'categories' to be an array:", data.categories);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filtered = categories.filter(
    (cat) =>
      (statusFilter === "" || cat.status === statusFilter) &&
      (search === "" || cat.name.toLowerCase().includes(search.toLowerCase()))
  );

  const handleStatusToggle = (id) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat._id === id
          ? { ...cat, status: cat.status === "Active" ? "Inactive" : "Active" }
          : cat
      )
    );
  };

  const toggleStatus = async (id) => {
    const updatedCategories = categories.map((cat) => {
      if (cat._id === id) {
        const newStatus = cat.status === "Active" ? "Inactive" : "Active";
        return { ...cat, status: newStatus };
      }
      return cat;
    });
    setCategories(updatedCategories);

    const updatedCategory = updatedCategories.find((cat) => cat._id === id);
    const newStatus = updatedCategory.status || "Active";

    try {
      const response = await fetch(
        `https://ecom-api.applligentdemo.com/category/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: ` ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) throw new Error("Failed to update status");
    } catch (error) {
      toast.error("Status update failed!");
      console.error(error);
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id
            ? {
                ...cat,
                status: newStatus === "Active" ? "Inactive" : "Active",
              }
            : cat
        )
      );
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      const res = await fetch(
        `https://ecom-api.applligentdemo.com/category/deleteCategory/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.ok) {
        toast.success("Category deleted successfully");
        setCategories((prev) => prev.filter((cat) => cat._id !== id));
      } else {
        const data = await res.json();
        toast.error("Error: " + data.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        p={3}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Box display="flex" gap={2}>
            <Box
              display="flex"
              alignItems="center"
              bgcolor="#f1f1f1"
              px={2}
              borderRadius={1}
            >
              <SearchIcon />
              <InputBase
                placeholder="Search by category name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Select
              value={statusFilter}
              displayEmpty
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/add-category")}
          >
            + Add Category
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Category Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((cat, index) => (
                  <TableRow key={cat._id}>
                    <TableCell>
                      {(page - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      <img
                        src={cat.image}
                        width={60}
                        height={60}
                        alt="category"
                      />
                    </TableCell>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell>
                      {cat.description.length > 50
                        ? `${cat.description.slice(0, 50)}...`
                        : cat.description}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color={
                          cat.status === "Active" ? "success" : "secondary"
                        }
                        onClick={() => handleStatusToggle(cat._id)}
                      >
                        {cat.status}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <IconButton color="warning">
                        <MdModeEdit
                          onClick={() => navigate(`/edit-category/${cat._id}`)}
                        />
                      </IconButton>
                      <IconButton color="error">
                        <MdDelete onClick={() => deleteCategory(cat._id)} />
                      </IconButton>
                      <IconButton
                        color="info"
                        onClick={() => navigate(`/category/view/${cat._id}`)}
                      >
                        <MdVisibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(filtered.length / itemsPerPage)}
            page={page}
            onChange={(e, val) => setPage(val)}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default Category;
